import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Footer from '../components/Footer';
import ReCAPTCHA from 'react-google-recaptcha';

function FinancialLiteracyWorkshop() {
    const { programName } = useParams();
    const location = useLocation();
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [workshopData, setWorkshopData] = useState(null);
    const [bannerImages, setBannerImages] = useState([]);
    const [recaptchaValue, setRecaptchaValue] = useState(null);

    // Get programId and apiType from navigation state
    const programId = location.state?.programId;
    const apiType = location.state?.apiType || 'financial-workshop';

    // API URLs - Use specific ID if available, otherwise fetch all
    const API_URL = programId 
        ? `${process.env.REACT_APP_API_BASE_URL}/UI/financialWorkshop/${programId}`
        : `${process.env.REACT_APP_API_BASE_URL}/UI/financialWorkshop`;
    
    const REGISTRATION_API = `${process.env.REACT_APP_API_BASE_URL}/financialWorkshopApplication`;
    const API_MEDIA = `${process.env.REACT_APP_API_BASE_URL}/UImedia`;
    
    const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    // Form state
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        mobileNumber: '',
        occupation: '',
        workshopDay: '',
        timeSlot: '',
        workshopName: ''
    });

    // Fetch workshop data
    useEffect(() => {
        fetchWorkshopData();
    }, [programId]); // Refetch if programId changes

    const fetchWorkshopData = async () => {
        try {
            setLoading(true);
            // console.log('Fetching workshop data from:', API_URL);
            // console.log('Program ID:', programId);

            // Fetch workshop data
            const response = await axios.get(API_URL);
            // console.log('API Response:', response.data);

            if (response.data.success && response.data.data) {
                const data = response.data.data;
                setWorkshopData(data);

                // Check for banner images in the data
                if (data.bannerImages && data.bannerImages.length > 0) {
                    await fetchBannerImages(data.bannerImages);
                } else {
                    setBannerImages([]);
                }

                // Set default form values
                if (data.workshopInfo?.title) {
                    setFormData(prev => ({
                        ...prev,
                        workshopName: data.workshopInfo.title
                    }));
                }

                if (data.workshopDates && data.workshopDates.length > 0) {
                    setFormData(prev => ({
                        ...prev,
                        workshopDay: data.workshopDates[0]
                    }));
                }

                if (data.timeSlots && data.timeSlots.length > 0) {
                    setFormData(prev => ({
                        ...prev,
                        timeSlot: data.timeSlots[0]
                    }));
                }

                // If we have a single workshop object (not array), use it directly
                if (!Array.isArray(data)) {
                    // console.log('Single workshop found:', data.workshopInfo?.title);
                } else if (data.length > 0) {
                    // If it's an array, take the first active workshop
                    const activeWorkshop = data.find(w => w.isActive === true) || data[0];
                    // console.log('Array of workshops, using:', activeWorkshop.workshopInfo?.title);
                }

            } else if (response.data.data && Array.isArray(response.data.data)) {
                // Handle array response
                const workshops = response.data.data;
                
                // Find the specific workshop by ID if provided
                let workshop;
                if (programId) {
                    workshop = workshops.find(w => 
                        w._id === programId || 
                        w.id === programId ||
                        (w.workshopInfo && w.workshopInfo._id === programId)
                    );
                }
                
                // If not found by ID, get first active workshop
                if (!workshop) {
                    workshop = workshops.find(w => w.isActive === true) || workshops[0];
                }
                
                if (workshop) {
                    setWorkshopData(workshop);
                    
                    // Set banner images
                    if (workshop.bannerImages && workshop.bannerImages.length > 0) {
                        await fetchBannerImages(workshop.bannerImages);
                    }
                    
                    // Set form values
                    if (workshop.workshopInfo?.title) {
                        setFormData(prev => ({
                            ...prev,
                            workshopName: workshop.workshopInfo.title
                        }));
                    }
                    
                    if (workshop.workshopDates && workshop.workshopDates.length > 0) {
                        setFormData(prev => ({
                            ...prev,
                            workshopDay: workshop.workshopDates[0]
                        }));
                    }
                    
                    if (workshop.timeSlots && workshop.timeSlots.length > 0) {
                        setFormData(prev => ({
                            ...prev,
                            timeSlot: workshop.timeSlots[0]
                        }));
                    }
                } else {
                    throw new Error('No workshop found');
                }
            } else {
                throw new Error('Invalid workshop data format');
            }
        } catch (err) {
            console.error('Error fetching workshop data:', err);
            setError('Failed to load workshop data. Please try again later.');
            setBannerImages([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch banner images from Media collection
    const fetchBannerImages = async (bannerImageArray) => {
        try {
            const imageUrls = [];
            const token = localStorage.getItem('token');

            // Loop through each banner and fetch media
            for (const banner of bannerImageArray) {
                if (banner.mediaId) {
                    try {
                        const imageUrl = await fetchImageFromMedia(banner.mediaId, token);
                        if (imageUrl) {
                            imageUrls.push(imageUrl);
                        }
                    } catch (imgErr) {
                        console.error('Error fetching image for mediaId:', banner.mediaId, imgErr);
                    }
                }
            }

            setBannerImages(imageUrls);

        } catch (error) {
            console.error('Error in fetchBannerImages:', error);
            setBannerImages([]);
        }
    };

    // Fetch image from UImedia endpoint
    const fetchImageFromMedia = async (mediaId, token) => {
        try {
            const headers = {};
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            // TRY METHOD 1: Direct download endpoint
            try {
                const response = await axios.get(
                    `${API_MEDIA}/${mediaId.id}/download`,
                    {
                        responseType: 'blob',
                        headers: headers,
                        timeout: 15000
                    }
                );

                if (response.data && response.status === 200) {
                    const imageUrl = URL.createObjectURL(response.data);
                    return imageUrl;
                }
            } catch (downloadErr) {
                // TRY METHOD 2: Get media info first
                try {
                    const infoResponse = await axios.get(
                        `${API_MEDIA}/${mediaId}`,
                        {
                            headers: headers,
                            timeout: 10000
                        }
                    );

                    if (infoResponse.data.success && infoResponse.data.data) {
                        const mediaData = infoResponse.data.data;

                        // Check for different possible URL fields
                        if (mediaData.url) {
                            return mediaData.url;
                        } else if (mediaData.imageUrl) {
                            return mediaData.imageUrl;
                        } else if (mediaData.downloadUrl) {
                            return mediaData.downloadUrl;
                        } else if (mediaData.fileUrl) {
                            return mediaData.fileUrl;
                        }
                    }
                } catch (infoErr) {
                    console.error(`Error getting media info for ${mediaId}:`, infoErr);
                }
            }

            return null;
        } catch (err) {
            console.error(`Error in fetchImageFromMedia for ${mediaId}:`, err);
            return null;
        }
    };

    // Auto slide functionality
    useEffect(() => {
        if (bannerImages.length > 1) {
            const interval = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % bannerImages.length);
            }, 5000);

            return () => clearInterval(interval);
        }
    }, [bannerImages.length]);

    // Cleanup object URLs
    useEffect(() => {
        return () => {
            bannerImages.forEach(url => {
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [bannerImages]);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Handle reCAPTCHA change
    const handleRecaptchaChange = (value) => {
        setRecaptchaValue(value);
    };

    // Handle reCAPTCHA expire
    const handleRecaptchaExpire = () => {
        setRecaptchaValue(null);
        alert('reCAPTCHA has expired. Please verify again.');
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!workshopData) {
            alert('Workshop data not loaded. Please try again.');
            return;
        }

        // Basic validation
        if (!formData.fullName.trim()) {
            alert('Please enter your full name');
            return;
        }
        if (!formData.email.trim()) {
            alert('Please enter your email');
            return;
        }
        if (!formData.mobileNumber.trim() || !/^\d{10}$/.test(formData.mobileNumber)) {
            alert('Please enter a valid 10-digit mobile number');
            return;
        }
        if (!formData.occupation.trim()) {
            alert('Please enter your occupation');
            return;
        }

        // reCAPTCHA validation
        if (!recaptchaValue) {
            alert('Please complete the reCAPTCHA verification');
            return;
        }

        setIsSubmitting(true);

        try {
            // Prepare data with reCAPTCHA token
            const registrationData = {
                ...formData,
                workshopName: workshopData.workshopInfo?.title || 'Financial Literacy Workshop',
                recaptchaToken: recaptchaValue
            };

            // console.log('Sending registration to:', REGISTRATION_API);
            // console.log('Registration data:', registrationData);

            const registrationResponse = await axios.post(
                REGISTRATION_API, 
                registrationData,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            // console.log('Registration response:', registrationResponse.data);

            if (registrationResponse.data.success) {
                setShowSuccessPopup(true);

                // Reset form but keep workshop name
                setFormData({
                    fullName: '',
                    email: '',
                    mobileNumber: '',
                    occupation: '',
                    workshopDay: workshopData.workshopDates?.[0] || '',
                    timeSlot: workshopData.timeSlots?.[0] || '',
                    workshopName: workshopData.workshopInfo?.title || ''
                });

                // Reset reCAPTCHA
                setRecaptchaValue(null);
                
                // Reset reCAPTCHA component
                if (window.grecaptcha) {
                    window.grecaptcha.reset();
                }
            } else {
                alert(registrationResponse.data.message || 'Registration failed. Please try again.');
            }
        } catch (err) {
            console.error('Registration error:', err);
            
            // Handle specific errors
            if (err.response && err.response.data && err.response.data.message) {
                alert(`Error: ${err.response.data.message}`);
            } else if (err.response && err.response.data && err.response.data.error) {
                alert(`Error: ${err.response.data.error}`);
            } else {
                alert('Registration failed. Please check your internet connection and try again.');
            }
            
            // Reset reCAPTCHA on error
            setRecaptchaValue(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Slide navigation
    const nextSlide = () => {
        if (bannerImages.length > 0) {
            setCurrentSlide((prev) => (prev + 1) % bannerImages.length);
        }
    };

    const prevSlide = () => {
        if (bannerImages.length > 0) {
            setCurrentSlide((prev) => (prev - 1 + bannerImages.length) % bannerImages.length);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-amber-300 text-lg">Loading Financial Workshop...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !workshopData) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-8">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-2xl text-red-400">!</span>
                    </div>
                    <h3 className="text-2xl font-bold text-red-400 mb-4">Workshop Not Available</h3>
                    <p className="text-amber-100 mb-6">{error || 'No active workshop found at the moment.'}</p>
                    <button
                        onClick={fetchWorkshopData}
                        className="bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold py-3 px-6 rounded-lg hover:from-amber-600 hover:to-yellow-600 transition-all"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white">
            {/* Dynamic Banner Section */}
            <header className="relative h-[400px] md:h-[500px] overflow-hidden">
                {/* Image Slides */}
                <div className="relative h-full">
                    {bannerImages.map((image, index) => (
                        <div
                            key={index}
                            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                                }`}
                        >
                            <div
                                className="absolute inset-0"
                                style={{
                                    backgroundImage: `url(${image})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat'
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
                            </div>
                        </div>
                    ))}

                    {/* Show message if no banners available */}
                    {bannerImages.length === 0 && (
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-gray-800 flex items-center justify-center z-10">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-2xl text-gray-400">📷</span>
                                </div>
                                <p className="text-gray-400 text-xl">No banner images available</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation Arrows - Only show if we have banners */}
                {bannerImages.length > 1 && (
                    <>
                        <button
                            onClick={prevSlide}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/40 hover:bg-black/60 text-white p-2 md:p-3 rounded-full transition-all hover:scale-110"
                            aria-label="Previous slide"
                        >
                            ←
                        </button>

                        <button
                            onClick={nextSlide}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/40 hover:bg-black/60 text-white p-2 md:p-3 rounded-full transition-all hover:scale-110"
                            aria-label="Next slide"
                        >
                            →
                        </button>
                    </>
                )}

                {/* Dots Indicator - Only show if we have banners */}
                {bannerImages.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
                        {bannerImages.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`w-3 h-3 rounded-full transition-all ${index === currentSlide
                                        ? 'bg-amber-400 scale-125'
                                        : 'bg-white/50 hover:bg-white/70'
                                    }`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                )}

                {/* Main Content Overlay */}
                <div className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="max-w-6xl mx-auto px-4 text-center">
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white">
                            {workshopData.workshopInfo?.title || 'Financial Literacy Workshop'}
                        </h1>
                        <p className="text-lg md:text-xl lg:text-2xl text-amber-300 mb-6 md:mb-8 max-w-3xl mx-auto">
                            {workshopData.workshopInfo?.subtitle || "Build smart money habits. Make informed financial decisions."}
                        </p>
                        <button
                            onClick={() => document.getElementById('registration-form').scrollIntoView({ behavior: 'smooth' })}
                            className="bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold py-3 px-6 md:py-4 md:px-8 rounded-full text-lg hover:from-amber-600 hover:to-yellow-600 transition-all transform hover:scale-105"
                        >
                            Register Now
                        </button>
                    </div>
                </div>
            </header>

            {/* Rest of the component */}
            <main className="max-w-6xl mx-auto px-4 py-12">
                {/* Page Title Section */}
                <section className="mb-16 text-center">
                    <h2 className="text-4xl font-bold mb-6 text-amber-300">
                        {workshopData.workshopInfo?.title || 'Financial Literacy Workshop'}
                    </h2>
                    <p className="text-xl text-amber-200 max-w-4xl mx-auto">
                        {workshopData.workshopInfo?.subtitle || "A practical workshop to improve your financial knowledge"}
                    </p>
                </section>

                {/* Description Section */}
                <section className="mb-16">
                    <div className="bg-gradient-to-br from-gray-800/70 to-gray-900/70 backdrop-blur-sm rounded-xl p-8 border border-amber-500/20">
                        <h3 className="text-2xl font-bold mb-6 text-amber-300">
                            Workshop Description
                        </h3>
                        <div className="text-lg text-white space-y-4">
                            <p>
                                {workshopData.workshopInfo?.description || "This workshop provides practical financial knowledge and skills to help you manage your money better, plan for the future, and make informed financial decisions."}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Who Can Participate */}
                {workshopData.participants && workshopData.participants.length > 0 && (
                    <section className="mb-16">
                        <h3 className="text-3xl font-bold mb-8 text-center text-amber-300">
                            Who Can Participate?
                        </h3>

                        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-amber-500/20">
                            <p className="text-lg text-amber-200 mb-8 text-center">
                                This workshop is open to all individuals who wish to improve their financial knowledge and money management skills.
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {workshopData.participants.map((item, index) => (
                                    <div key={index} className="flex items-start gap-3 p-4 bg-gray-700/30 rounded-lg border border-gray-600">
                                        <span className="text-amber-400 font-bold">•</span>
                                        <span className="text-amber-100">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Workshop Details */}
                {workshopData.workshopDetails && workshopData.workshopDetails.length > 0 && (
                    <section className="mb-16">
                        <h3 className="text-3xl font-bold mb-8 text-center text-amber-300">
                            Workshop Details
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {workshopData.workshopDetails.map((detail, index) => (
                                <div key={index} className="bg-gradient-to-br from-gray-800 to-gray-900/50 p-8 rounded-xl border border-amber-500/20 text-center">
                                    <div className="text-amber-400 mb-4 flex justify-center">
                                        <span className="text-4xl font-bold">{detail.number}</span>
                                    </div>
                                    <h4 className="text-xl font-bold text-amber-200 mb-2">
                                        {detail.title}
                                    </h4>
                                    <p className="text-amber-100">{detail.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Registration Form */}
                <section id="registration-form" className="mb-20">
                    <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-8 border border-amber-500/30">
                        <h2 className="text-3xl font-bold mb-8 text-center text-amber-300">
                            Registration Form
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-8 max-w-2xl mx-auto">
                            {/* Hidden workshop name field */}
                            <input
                                type="hidden"
                                name="workshopName"
                                value={workshopData.workshopInfo?.title || 'Financial Literacy Workshop'}
                            />

                            {/* Personal Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-amber-300 mb-2 font-medium">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                                        placeholder="Enter your full name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-amber-300 mb-2 font-medium">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                                        placeholder="Enter your email"
                                    />
                                </div>

                                <div>
                                    <label className="block text-amber-300 mb-2 font-medium">
                                        Mobile Number *
                                    </label>
                                    <input
                                        type="tel"
                                        name="mobileNumber"
                                        value={formData.mobileNumber}
                                        onChange={handleInputChange}
                                        required
                                        pattern="[0-9]{10}"
                                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                                        placeholder="Enter 10-digit mobile number"
                                    />
                                </div>

                                <div>
                                    <label className="block text-amber-300 mb-2 font-medium">
                                        Occupation *
                                    </label>
                                    <input
                                        type="text"
                                        name="occupation"
                                        value={formData.occupation}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                                        placeholder="e.g., Business Owner, Working Professional, Student"
                                    />
                                </div>
                            </div>

                            {/* Workshop Day Selection */}
                            {workshopData.workshopDates && workshopData.workshopDates.length > 0 && (
                                <div>
                                    <label className="block text-amber-300 mb-3 font-medium">
                                        Select Workshop Day *
                                    </label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        {workshopData.workshopDates.map((date) => (
                                            <label
                                                key={date}
                                                className={`flex items-center justify-center gap-3 p-4 rounded-lg cursor-pointer transition-colors ${formData.workshopDay === date
                                                        ? 'bg-amber-500/20 border border-amber-500/30'
                                                        : 'bg-gray-700/30 border border-gray-600 hover:bg-gray-700/50'
                                                    }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="workshopDay"
                                                    value={date}
                                                    checked={formData.workshopDay === date}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="text-amber-500 focus:ring-amber-500"
                                                />
                                                <span className="text-amber-100">{date}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Time Slot Selection */}
                            {workshopData.timeSlots && workshopData.timeSlots.length > 0 && (
                                <div>
                                    <label className="block text-amber-300 mb-3 font-medium">
                                        Select Preferred Time Slot *
                                    </label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        {workshopData.timeSlots.map((timeSlot) => (
                                            <label
                                                key={timeSlot}
                                                className={`flex items-center justify-center gap-3 p-4 rounded-lg cursor-pointer transition-colors ${formData.timeSlot === timeSlot
                                                        ? 'bg-amber-500/20 border border-amber-500/30'
                                                        : 'bg-gray-700/30 border border-gray-600 hover:bg-gray-700/50'
                                                    }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="timeSlot"
                                                    value={timeSlot}
                                                    checked={formData.timeSlot === timeSlot}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="text-amber-500 focus:ring-amber-500"
                                                />
                                                <span className="text-amber-100">{timeSlot}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* reCAPTCHA Section */}
                            {RECAPTCHA_SITE_KEY && (
                                <div className="pt-4">
                                    <div className="flex justify-center">
                                        <ReCAPTCHA
                                            sitekey={RECAPTCHA_SITE_KEY}
                                            onChange={handleRecaptchaChange}
                                            onExpired={handleRecaptchaExpire}
                                            theme="dark"
                                            size="normal"
                                        />
                                    </div>
                                    <p className="text-sm text-amber-400/70 text-center mt-2">
                                        Please verify that you are not a robot
                                    </p>
                                </div>
                            )}

                            {/* Workshop Info Display */}
                            <div className="bg-gray-800/30 rounded-lg p-4 border border-amber-500/20">
                                <p className="text-amber-300 font-medium mb-2">You are registering for:</p>
                                <p className="text-amber-100 text-lg font-semibold">
                                    {workshopData.workshopInfo?.title || 'Financial Literacy Workshop'}
                                </p>
                            </div>

                            {/* Submit Button */}
                            <div className="text-center pt-6">
                                <button
                                    type="submit"
                                    disabled={isSubmitting || (!RECAPTCHA_SITE_KEY ? false : !recaptchaValue)}
                                    className={`w-full md:w-auto bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold py-4 px-8 rounded-lg transition-all transform focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${isSubmitting || (!RECAPTCHA_SITE_KEY ? false : !recaptchaValue)
                                            ? 'opacity-50 cursor-not-allowed'
                                            : 'hover:from-amber-600 hover:to-yellow-600 hover:scale-105'
                                        }`}
                                >
                                    {isSubmitting ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <span className="animate-pulse">●</span>
                                            PROCESSING REGISTRATION...
                                        </span>
                                    ) : (
                                        'Confirm Registration'
                                    )}
                                </button>

                                <p className="mt-4 text-sm text-amber-400/70">
                                    {workshopData.successMessage || "Limited seats available. Registration is mandatory to attend the workshop."}
                                </p>
                            </div>
                        </form>
                    </div>
                </section>
            </main>

            {/* Footer Note */}
            <footer className="bg-gray-900 border-t border-amber-500/20 py-8">
                <div className="max-w-6xl mx-auto px-4 text-center">
                    <p className="text-amber-200">
                        {workshopData.successMessage || "Limited seats available. Registration is mandatory to attend the workshop."}
                    </p>
                </div>
            </footer>

            <Footer />

            {/* Success Popup Modal */}
            {showSuccessPopup && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-amber-500/30 rounded-2xl max-w-md w-full p-8">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="text-2xl text-white">✓</span>
                            </div>

                            <h3 className="text-2xl font-bold text-amber-300 mb-4">
                                Registration Successful!
                            </h3>

                            <p className="text-amber-100 mb-6">
                                Thank you for registering for <strong>{workshopData.workshopInfo?.title || 'Financial Literacy Workshop'}</strong>. 
                                A confirmation email has been sent to your email address.
                            </p>

                            <div className="bg-gray-800/30 rounded-lg p-4 mb-6 text-left">
                                <p className="text-amber-300 font-medium">Registration Details:</p>
                                <p className="text-amber-100">Workshop: {workshopData.workshopInfo?.title || 'Financial Literacy Workshop'}</p>
                                <p className="text-amber-100">Date: {formData.workshopDay}</p>
                                <p className="text-amber-100">Time: {formData.timeSlot}</p>
                            </div>

                            <button
                                onClick={() => setShowSuccessPopup(false)}
                                className="bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold py-3 px-8 rounded-lg hover:from-amber-600 hover:to-yellow-600 transition-all w-full"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FinancialLiteracyWorkshop;